import { useState, useCallback, useId } from 'react';
import { Box, Typography } from '../../../components/ui';
import type { SOPRunbookFormData, StepFormData, EmergencyContactFormData } from '../../../types/knowledgeBase';
import { StepEditor } from './StepEditor';
import './SOPRunbookEditor.css';

interface SOPRunbookEditorProps {
  content: SOPRunbookFormData;
  onChange: (content: SOPRunbookFormData) => void;
  errors?: {
    objective?: string;
    prerequisites?: string;
    procedureSteps?: string;
    verification?: string;
    emergencyContacts?: string;
  };
}

const EMPTY_STEP: StepFormData = {
  title: '',
  content: '',
};

const EMPTY_CONTACT: EmergencyContactFormData = {
  name: '',
  role: '',
  contact: '',
};

export function SOPRunbookEditor({ content, onChange, errors = {} }: SOPRunbookEditorProps) {
  const [prereqInput, setPrereqInput] = useState('');

  // Generate unique IDs for accessibility
  const baseId = useId();
  const objectiveId = `${baseId}-objective`;
  const prereqInputId = `${baseId}-prereq`;
  const verificationId = `${baseId}-verification`;
  const rollbackId = `${baseId}-rollback`;

  // Prerequisite handlers
  const handleAddPrereq = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const prereq = prereqInput.trim();
        if (prereq && !content.prerequisites.includes(prereq)) {
          onChange({
            ...content,
            prerequisites: [...content.prerequisites, prereq],
          });
        }
        setPrereqInput('');
      }
    },
    [content, onChange, prereqInput]
  );

  const handleRemovePrereq = useCallback(
    (prereq: string) => {
      onChange({
        ...content,
        prerequisites: content.prerequisites.filter((p) => p !== prereq),
      });
    },
    [content, onChange]
  );

  // Field change handler
  const handleFieldChange = useCallback(
    (field: keyof SOPRunbookFormData, value: string) => {
      onChange({ ...content, [field]: value });
    },
    [content, onChange]
  );

  // Procedure step handlers
  const handleStepChange = useCallback(
    (index: number, step: StepFormData) => {
      const newSteps = content.procedureSteps.map((s, i) => (i === index ? step : s));
      onChange({ ...content, procedureSteps: newSteps });
    },
    [content, onChange]
  );

  const handleAddStep = useCallback(() => {
    onChange({
      ...content,
      procedureSteps: [...content.procedureSteps, { ...EMPTY_STEP }],
    });
  }, [content, onChange]);

  const handleRemoveStep = useCallback(
    (index: number) => {
      onChange({
        ...content,
        procedureSteps: content.procedureSteps.filter((_, i) => i !== index),
      });
    },
    [content, onChange]
  );

  const handleMoveStep = useCallback(
    (index: number, direction: 'up' | 'down') => {
      const newSteps = [...content.procedureSteps];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= newSteps.length) return;
      [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
      onChange({ ...content, procedureSteps: newSteps });
    },
    [content, onChange]
  );

  // Emergency contact handlers
  const handleContactChange = useCallback(
    (index: number, field: keyof EmergencyContactFormData, value: string) => {
      const newContacts = content.emergencyContacts.map((c, i) =>
        i === index ? { ...c, [field]: value } : c
      );
      onChange({ ...content, emergencyContacts: newContacts });
    },
    [content, onChange]
  );

  const handleAddContact = useCallback(() => {
    onChange({
      ...content,
      emergencyContacts: [...content.emergencyContacts, { ...EMPTY_CONTACT }],
    });
  }, [content, onChange]);

  const handleRemoveContact = useCallback(
    (index: number) => {
      onChange({
        ...content,
        emergencyContacts: content.emergencyContacts.filter((_, i) => i !== index),
      });
    },
    [content, onChange]
  );

  return (
    <Box className="sop-runbook-editor">
      {/* Objective */}
      <Box className="sop-runbook-editor__section">
        <Box className="sop-runbook-editor__section-header">
          <label htmlFor={objectiveId} className="sop-runbook-editor__section-title">
            Objective
          </label>
          <span className="sop-runbook-editor__required" aria-hidden="true">
            Required
          </span>
        </Box>
        <Typography variant="body2" className="sop-runbook-editor__description">
          What is the goal or purpose of this procedure?
        </Typography>
        <textarea
          id={objectiveId}
          className="sop-runbook-editor__textarea"
          value={content.objective}
          onChange={(e) => handleFieldChange('objective', e.target.value)}
          placeholder="Example: This procedure ensures consistent server deployments with zero downtime..."
          rows={3}
          aria-required="true"
          aria-invalid={Boolean(errors.objective)}
          aria-describedby={errors.objective ? `${objectiveId}-error` : undefined}
        />
        {errors.objective && (
          <span id={`${objectiveId}-error`} className="sop-runbook-editor__error" role="alert">
            {errors.objective}
          </span>
        )}
      </Box>

      {/* Prerequisites */}
      <Box className="sop-runbook-editor__section">
        <Box className="sop-runbook-editor__section-header">
          <label htmlFor={prereqInputId} className="sop-runbook-editor__section-title">
            Prerequisites
          </label>
          <span className="sop-runbook-editor__required" aria-hidden="true">
            At least one
          </span>
        </Box>
        <Typography variant="body2" className="sop-runbook-editor__description">
          What must be in place before starting this procedure?
        </Typography>
        <Box
          className="sop-runbook-editor__prereq-list"
          role="list"
          aria-label="Prerequisites list"
        >
          {content.prerequisites.map((prereq, index) => (
            <Box key={index} className="sop-runbook-editor__prereq-item" role="listitem">
              <span className="sop-runbook-editor__prereq-text">{prereq}</span>
              <button
                className="sop-runbook-editor__prereq-remove"
                onClick={() => handleRemovePrereq(prereq)}
                type="button"
                aria-label={`Remove prerequisite: ${prereq}`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M3.5 3.5l7 7M3.5 10.5l7-7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </Box>
          ))}
          <input
            id={prereqInputId}
            type="text"
            className="sop-runbook-editor__prereq-input"
            value={prereqInput}
            onChange={(e) => setPrereqInput(e.target.value)}
            onKeyDown={handleAddPrereq}
            placeholder="Type a prerequisite and press Enter..."
            aria-invalid={Boolean(errors.prerequisites)}
          />
        </Box>
        {errors.prerequisites && (
          <span className="sop-runbook-editor__error" role="alert">
            {errors.prerequisites}
          </span>
        )}
      </Box>

      {/* Procedure Steps */}
      <Box className="sop-runbook-editor__section">
        <Box className="sop-runbook-editor__section-header">
          <Typography variant="subtitle1" className="sop-runbook-editor__section-title">
            Procedure Steps
          </Typography>
          <span className="sop-runbook-editor__required" aria-hidden="true">
            At least one
          </span>
        </Box>
        <Typography variant="body2" className="sop-runbook-editor__description">
          The sequential steps to complete this procedure.
        </Typography>
        <Box className="sop-runbook-editor__steps" role="list" aria-label="Procedure steps">
          {content.procedureSteps.map((step, index) => (
            <StepEditor
              key={step.id ?? index}
              step={step}
              stepNumber={index + 1}
              totalSteps={content.procedureSteps.length}
              onChange={(s) => handleStepChange(index, s)}
              onRemove={() => handleRemoveStep(index)}
              onMoveUp={() => handleMoveStep(index, 'up')}
              onMoveDown={() => handleMoveStep(index, 'down')}
              canRemove={content.procedureSteps.length > 1}
            />
          ))}
        </Box>
        <button
          className="sop-runbook-editor__add-step"
          onClick={handleAddStep}
          type="button"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M8 3v10M3 8h10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Add Step
        </button>
        {errors.procedureSteps && (
          <span className="sop-runbook-editor__error" role="alert">
            {errors.procedureSteps}
          </span>
        )}
      </Box>

      {/* Verification */}
      <Box className="sop-runbook-editor__section">
        <Box className="sop-runbook-editor__section-header">
          <label htmlFor={verificationId} className="sop-runbook-editor__section-title">
            Verification
          </label>
          <span className="sop-runbook-editor__required" aria-hidden="true">
            Required
          </span>
        </Box>
        <Typography variant="body2" className="sop-runbook-editor__description">
          How do you verify the procedure was completed successfully?
        </Typography>
        <textarea
          id={verificationId}
          className="sop-runbook-editor__textarea"
          value={content.verification}
          onChange={(e) => handleFieldChange('verification', e.target.value)}
          placeholder="Example: Verify the deployment by checking the health endpoint at /api/health returns 200 OK..."
          rows={3}
          aria-required="true"
          aria-invalid={Boolean(errors.verification)}
          aria-describedby={errors.verification ? `${verificationId}-error` : undefined}
        />
        {errors.verification && (
          <span id={`${verificationId}-error`} className="sop-runbook-editor__error" role="alert">
            {errors.verification}
          </span>
        )}
      </Box>

      {/* Rollback Procedure */}
      <Box className="sop-runbook-editor__section">
        <Box className="sop-runbook-editor__section-header">
          <label htmlFor={rollbackId} className="sop-runbook-editor__section-title">
            Rollback Procedure
          </label>
          <span className="sop-runbook-editor__optional" aria-hidden="true">
            Optional
          </span>
        </Box>
        <Typography variant="body2" className="sop-runbook-editor__description">
          Steps to revert changes if something goes wrong.
        </Typography>
        <textarea
          id={rollbackId}
          className="sop-runbook-editor__textarea"
          value={content.rollbackProcedure ?? ''}
          onChange={(e) => handleFieldChange('rollbackProcedure', e.target.value)}
          placeholder="Example: To rollback, run the deploy script with --rollback flag: ./deploy.sh --rollback..."
          rows={3}
        />
      </Box>

      {/* Emergency Contacts */}
      <Box className="sop-runbook-editor__section">
        <Box className="sop-runbook-editor__section-header">
          <Typography variant="subtitle1" className="sop-runbook-editor__section-title">
            Emergency Contacts
          </Typography>
          <span className="sop-runbook-editor__required" aria-hidden="true">
            At least one
          </span>
        </Box>
        <Typography variant="body2" className="sop-runbook-editor__description">
          Who to contact if issues arise during this procedure.
        </Typography>
        <Box className="sop-runbook-editor__contacts" role="list" aria-label="Emergency contacts">
          {content.emergencyContacts.map((contact, index) => {
            const contactNameId = `${baseId}-contact-${index}-name`;
            const contactRoleId = `${baseId}-contact-${index}-role`;
            const contactInfoId = `${baseId}-contact-${index}-info`;
            return (
              <Box key={index} className="sop-runbook-editor__contact-card" role="listitem">
                <Box className="sop-runbook-editor__contact-header">
                  <span className="sop-runbook-editor__contact-number">Contact {index + 1}</span>
                  {content.emergencyContacts.length > 1 && (
                    <button
                      className="sop-runbook-editor__contact-remove"
                      onClick={() => handleRemoveContact(index)}
                      type="button"
                      aria-label={`Remove contact ${index + 1}${contact.name ? `: ${contact.name}` : ''}`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M3.5 3.5l7 7M3.5 10.5l7-7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  )}
                </Box>
                <Box className="sop-runbook-editor__contact-fields">
                  <Box className="sop-runbook-editor__contact-field">
                    <label htmlFor={contactNameId} className="sop-runbook-editor__contact-label">
                      Name
                    </label>
                    <input
                      id={contactNameId}
                      type="text"
                      className="sop-runbook-editor__contact-input"
                      value={contact.name}
                      onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                      placeholder="John Smith"
                    />
                  </Box>
                  <Box className="sop-runbook-editor__contact-field">
                    <label htmlFor={contactRoleId} className="sop-runbook-editor__contact-label">
                      Role
                    </label>
                    <input
                      id={contactRoleId}
                      type="text"
                      className="sop-runbook-editor__contact-input"
                      value={contact.role}
                      onChange={(e) => handleContactChange(index, 'role', e.target.value)}
                      placeholder="DevOps Lead"
                    />
                  </Box>
                  <Box className="sop-runbook-editor__contact-field">
                    <label htmlFor={contactInfoId} className="sop-runbook-editor__contact-label">
                      Contact
                    </label>
                    <input
                      id={contactInfoId}
                      type="text"
                      className="sop-runbook-editor__contact-input"
                      value={contact.contact}
                      onChange={(e) => handleContactChange(index, 'contact', e.target.value)}
                      placeholder="john@company.com or +1-555-0123"
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
        <button
          className="sop-runbook-editor__add-contact"
          onClick={handleAddContact}
          type="button"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M8 3v10M3 8h10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Add Contact
        </button>
        {errors.emergencyContacts && (
          <span className="sop-runbook-editor__error" role="alert">
            {errors.emergencyContacts}
          </span>
        )}
      </Box>
    </Box>
  );
}
